/** @format */

import React, { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { 
  Bars3Icon, 
  XMarkIcon, 
  UserIcon, 
  ChevronDownIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  ChatBubbleLeftRightIcon,
  VideoCameraIcon,
  ShoppingBagIcon,
  DocumentTextIcon,
  RssIcon,
  SparklesIcon,
  UserGroupIcon,
  HeartIcon,
  PhotoIcon,
  ShoppingCartIcon
} from "@heroicons/react/24/outline";
import { useAuth } from "../contexts/AuthContext";
import { useCart } from "../contexts/CartContext";
import iconLogo from "../assets/icon.png";
import EmailVerificationWarning from "./EmailVerificationWarning";
import { shouldShowIOSWebViewAlert } from "../utils/deviceDetection";

const PublicHeader: React.FC = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const { currentUser, logout } = useAuth();
  const { cartItems, toggleCart } = useCart();
  const location = useLocation();
  
  // Always show header on mobile to ensure profile/cart are accessible
  const isDashboardPage = location.pathname.startsWith('/app');
  const isProfilePage = location.pathname === '/profile';
  const isLegalPage = location.pathname === '/legal';

  const handleSignOut = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const handleDropdownToggle = (dropdown: string) => {
    setActiveDropdown(activeDropdown === dropdown ? null : dropdown);
  };

  const handleDropdownClose = () => {
    setActiveDropdown(null);
  };

  // Hide header on mobile for dashboard pages, profile page, and legal page to prevent duplicate headers
  if ((isDashboardPage || isProfilePage || isLegalPage) && typeof window !== "undefined" && window.innerWidth < 1024) {
    return null;
  }

    return (
    <>
      <nav className="bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100 sticky top-0 z-50 mb-0">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            {/* Logo */}
            <div className="flex items-center">
              <Link to={currentUser ? "/app" : "/"} className="flex items-center group">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mr-3 shadow-lg group-hover:shadow-xl transition-all duration-300 group-hover:scale-105">
                  <img src={iconLogo} alt="ProfStudy Logo" className="w-10 h-10 rounded-xl" />
                </div>
                <span className="hidden sm:block text-2xl font-bold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent">
                  ProfStudy
                </span>
              </Link>
            </div>

          {/* Desktop Navigation Links - Dropdown Groups */}
          <div className="hidden md:flex items-center space-x-6">
            {/* Learning Dropdown */}
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('learning')}
                onMouseEnter={() => setActiveDropdown('learning')}
                className="flex items-center space-x-1 px-4 py-2 text-gray-700 hover:text-primary-600 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-primary-50 group"
              >
                <AcademicCapIcon className="h-4 w-4 mr-1" />
                <span>Learning</span>
                <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'learning' ? 'rotate-180' : ''}`} />
              </button>
              
              {activeDropdown === 'learning' && (
                <div 
                  className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50"
                  onMouseEnter={() => setActiveDropdown('learning')}
                  onMouseLeave={handleDropdownClose}
                >
                  {!shouldShowIOSWebViewAlert() && (
                    <Link
                      to="/catalog"
                      className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                      onClick={handleDropdownClose}
                    >
                      <AcademicCapIcon className="h-4 w-4 mr-2 text-primary-600" /> Courses
                    </Link>
                  )}
                  <Link
                    to="/classrooms"
                    className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                    onClick={handleDropdownClose}
                  >
                    <BuildingOfficeIcon className="h-4 w-4 mr-2 text-primary-600" /> Classrooms
                  </Link>
                  <div className="border-t border-gray-100 my-1"></div>
                  <Link
                    to="/study-forums"
                    className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                    onClick={handleDropdownClose}
                  >
                    <ChatBubbleLeftRightIcon className="h-4 w-4 mr-2 text-primary-600" /> Study Forums
                  </Link>
                  <Link
                    to="/live-meetings"
                    className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                    onClick={handleDropdownClose}
                  >
                    <VideoCameraIcon className="h-4 w-4 mr-2 text-primary-600" /> Live Meetings
                  </Link>
                </div>
              )}
            </div>

            {/* Resources Dropdown */}
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('resources')}
                onMouseEnter={() => setActiveDropdown('resources')}
                className="flex items-center space-x-1 px-4 py-2 text-gray-700 hover:text-primary-600 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-primary-50 group"
              >
                <DocumentTextIcon className="h-4 w-4 mr-1" />
                <span>Resources</span>
                <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'resources' ? 'rotate-180' : ''}`} />
              </button>
              
              {activeDropdown === 'resources' && (
                <div 
                  className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50"
                  onMouseEnter={() => setActiveDropdown('resources')}
                  onMouseLeave={handleDropdownClose}
                >
                  {!shouldShowIOSWebViewAlert() && (
                    <Link
                      to="/shop"
                      className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                      onClick={handleDropdownClose}
                    >
                      <ShoppingBagIcon className="h-4 w-4 mr-2 text-primary-600" /> Shop
                    </Link>
                  )}
                  <Link
                    to="/blog"
                    className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                    onClick={handleDropdownClose}
                  >
                    <DocumentTextIcon className="h-4 w-4 mr-2 text-primary-600" /> Blog
                  </Link>
                  <div className="border-t border-gray-100 my-1"></div>
                  <Link
                    to="/feed"
                    className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                    onClick={handleDropdownClose}
                  >
                    <RssIcon className="h-4 w-4 mr-2 text-primary-600" /> General Feed
                  </Link>
                  <Link
                    to="/ai-assistant"
                    className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                    onClick={handleDropdownClose}
                  >
                    <SparklesIcon className="h-4 w-4 mr-2 text-primary-600" /> Profstudymate
                  </Link>
                </div>
              )}
            </div>

            {/* Community Dropdown */}
            <div className="relative">
              <button
                onClick={() => handleDropdownToggle('community')}
                onMouseEnter={() => setActiveDropdown('community')}
                className="flex items-center space-x-1 px-4 py-2 text-gray-700 hover:text-primary-600 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-primary-50 group"
              >
                <UserGroupIcon className="h-4 w-4 mr-1" />
                <span>Community</span>
                <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${activeDropdown === 'community' ? 'rotate-180' : ''}`} />
              </button>
              
              {activeDropdown === 'community' && (
                <div 
                  className="absolute top-full left-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50"
                  onMouseEnter={() => setActiveDropdown('community')}
                  onMouseLeave={handleDropdownClose}
                >
                  <Link
                    to="/affiliate"
                    className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                    onClick={handleDropdownClose}
                  >
                    <UserGroupIcon className="h-4 w-4 mr-2 text-primary-600" /> Refer & Earn
                  </Link>
                  <Link
                    to="/testimonials"
                    className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                    onClick={handleDropdownClose}
                  >
                    <HeartIcon className="h-4 w-4 mr-2 text-primary-600" /> Testimonials
                  </Link>
                  <div className="border-t border-gray-100 my-1"></div>
                  <Link
                    to="/gallery"
                    className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                    onClick={handleDropdownClose}
                  >
                    <PhotoIcon className="h-4 w-4 mr-2 text-primary-600" /> Gallery
                  </Link>
                  <Link
                    to="/mentorship"
                    className="block px-4 py-2 text-sm text-gray-700 hover:text-primary-600 hover:bg-primary-50 transition-colors duration-200"
                    onClick={handleDropdownClose}
                  >
                    <UserIcon className="h-4 w-4 mr-2 text-primary-600" /> Mentorship
                  </Link>
                </div>
              )}
            </div>
          </div>

          {/* Legal Link */}
          <div className="hidden md:flex items-center">
            <Link
              to="/legal"
              className="text-gray-600 hover:text-primary-600 text-sm font-medium transition-all duration-200 px-3 py-2 rounded-lg hover:bg-primary-50 flex items-center"
            >
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              Legal Info
            </Link>
          </div>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-3">
            {/* Cart Icon - Only show for authenticated users and not on iOS WebView */}
            {currentUser && !shouldShowIOSWebViewAlert() && (
              <button
                onClick={toggleCart}
                className="relative p-2 text-gray-500 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
              >
                <ShoppingCartIcon className="h-5 w-5" />
                {cartItems.length > 0 && (
                  <span className="absolute -top-1 -right-1 w-4 h-4 bg-primary-600 text-white text-xs rounded-full flex items-center justify-center">
                    {cartItems.length}
                  </span>
                )}
              </button>
            )}

            {currentUser ? (
              <>
                {/* Visual Separator */}
                <div className="w-px h-8 bg-gray-200"></div>
                
                {/* User Account Group */}
                <div className="flex items-center space-x-1">
                  <Link
                    to="/profile"
                    className="hidden sm:flex items-center text-gray-700 hover:text-primary-600 px-4 py-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-primary-50 group"
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center mr-2 overflow-hidden border-2 border-gray-200 group-hover:border-primary-300 transition-all duration-200">
                      {currentUser?.avatar ? (
                        <img 
                          src={currentUser.avatar} 
                          alt={`${currentUser.name || 'User'} profile`}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center group-hover:from-primary-200 group-hover:to-primary-300 transition-all duration-200">
                          <UserIcon className="h-4 w-4 text-primary-600" />
                        </div>
                      )}
                    </div>
                    Profile
                  </Link>
                  <button
                    onClick={handleSignOut}
                    className="hidden sm:block text-gray-700 hover:text-red-600 p-2 text-sm font-medium transition-all duration-200 rounded-lg hover:bg-red-50"
                    title="Sign Out"
                  >
                    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </div>
                
                {/* Mobile Profile Button */}
                <Link
                  to="/profile"
                  className="sm:hidden w-10 h-10 rounded-full flex items-center justify-center overflow-hidden border-2 border-gray-200 hover:border-primary-300 transition-all duration-200"
                >
                  {currentUser?.avatar ? (
                    <img 
                      src={currentUser.avatar} 
                      alt={`${currentUser.name || 'User'} profile`}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    <div className="w-full h-full bg-gradient-to-br from-primary-100 to-primary-200 rounded-full flex items-center justify-center hover:from-primary-200 hover:to-primary-300 transition-all duration-200">
                      <UserIcon className="h-5 w-5 text-primary-600" />
                    </div>
                  )}
                </Link>
              </>
            ) : (
              <>
                <Link
                  to="/signup"
                  className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white px-6 py-2.5 text-sm font-semibold rounded-lg shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200"
                >
                  Get Started
                </Link>
              </>
            )}

            {/* Mobile menu button */}
            <button
              type="button"
              className="md:hidden inline-flex items-center justify-center p-2 rounded-lg text-gray-700 hover:text-primary-600 hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 transition-all duration-200"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              <span className="sr-only">Open main menu</span>
              {mobileMenuOpen ? (
                <XMarkIcon className="block h-6 w-6" />
              ) : (
                <Bars3Icon className="block h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile menu - Grouped */}
        {mobileMenuOpen && (
          <div className="md:hidden">
            <div className="px-2 pt-2 pb-3 space-y-3 bg-white/95 backdrop-blur-md rounded-b-xl shadow-xl border border-gray-100">
              {/* Learning Group */}
              <div className="px-3 py-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                  <AcademicCapIcon className="h-3 w-3 mr-2" />
                  Learning
                </h3>
                <div className="space-y-2">
                  {!shouldShowIOSWebViewAlert() && (
                    <Link
                      to="/catalog"
                      className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <AcademicCapIcon className="h-4 w-4 mr-3 text-primary-600" /> Courses
                    </Link>
                  )}
                  <Link
                    to="/classrooms"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <BuildingOfficeIcon className="h-4 w-4 mr-3 text-primary-600" /> Classrooms
                  </Link>
                  <Link
                    to="/study-forums"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <ChatBubbleLeftRightIcon className="h-4 w-4 mr-3 text-primary-600" /> Study Forums
                  </Link>
                  <Link
                    to="/live-meetings"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <VideoCameraIcon className="h-4 w-4 mr-3 text-primary-600" /> Live Meetings
                  </Link>
                </div>
              </div>

              {/* Visual Separator */}
              <div className="border-t border-gray-200 mx-3"></div>

              {/* Resources Group */}
              <div className="px-3 py-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                  <DocumentTextIcon className="h-3 w-3 mr-2" />
                  Resources
                </h3>
                <div className="space-y-2">
                  {!shouldShowIOSWebViewAlert() && (
                    <Link
                      to="/shop"
                      className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                      onClick={() => setMobileMenuOpen(false)}
                    >
                      <ShoppingBagIcon className="h-4 w-4 mr-3 text-primary-600" /> Shop
                    </Link>
                  )}
                  <Link
                    to="/blog"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <DocumentTextIcon className="h-4 w-4 mr-3 text-primary-600" /> Blog
                  </Link>
                  <Link
                    to="/feed"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <RssIcon className="h-4 w-4 mr-3 text-primary-600" /> General Feed
                  </Link>
                  <Link
                    to="/ai-assistant"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <SparklesIcon className="h-4 w-4 mr-3 text-primary-600" /> Profstudymate
                  </Link>
                </div>
              </div>

              {/* Visual Separator */}
              <div className="border-t border-gray-200 mx-3"></div>

              {/* Community Group */}
              <div className="px-3 py-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                  <UserGroupIcon className="h-3 w-3 mr-2" />
                  Community
                </h3>
                <div className="space-y-2">
                  <Link
                    to="/affiliate"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <UserGroupIcon className="h-4 w-4 mr-3 text-primary-600" /> Refer & Earn
                  </Link>
                  <Link
                    to="/testimonials"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <HeartIcon className="h-4 w-4 mr-3 text-primary-600" /> Testimonials
                  </Link>
                  <Link
                    to="/gallery"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <PhotoIcon className="h-4 w-4 mr-3 text-primary-600" /> Gallery
                  </Link>
                  <Link
                    to="/mentorship"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <UserIcon className="h-4 w-4 mr-3 text-primary-600" /> Mentorship
                  </Link>
                </div>
              </div>

              {/* Legal Section */}
              <div className="px-3 py-2">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                  <DocumentTextIcon className="h-3 w-3 mr-2" />
                  Legal
                </h3>
                <div className="space-y-2">
                  <Link
                    to="/legal"
                    className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <DocumentTextIcon className="h-4 w-4 mr-3 text-primary-600" /> Legal Info
                  </Link>
                </div>
              </div>

              {/* User Section */}
              {currentUser && (
                <>
                  {/* Visual Separator */}
                  <div className="border-t border-gray-200 mx-3"></div>
                  
                  <div className="px-3 py-2">
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3 flex items-center">
                      <UserIcon className="h-3 w-3 mr-2" />
                      Account
                    </h3>
                    <div className="space-y-2">
                      <Link
                        to="/profile"
                        className="block px-4 py-3 text-base font-medium text-gray-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all duration-200"
                        onClick={() => {
                          setMobileMenuOpen(false);
                        }}
                      >
                        <UserIcon className="h-4 w-4 mr-3 text-primary-600" /> Profile
                      </Link>
                      <button
                        onClick={() => {
                          handleSignOut();
                          setMobileMenuOpen(false);
                        }}
                        className="block w-full text-left px-4 py-3 text-base font-medium text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-all duration-200"
                        title="Sign Out"
                      >
                        <svg className="h-5 w-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Email Verification Warning */}
      <EmailVerificationWarning location="header" />
    </nav>
    </>
  );
};

export default PublicHeader;
